import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Save,
  RotateCcw,
  Upload,
  Building2,
  MapPin,
  Share2,
  Lock,
  X,
  Image as ImageIcon,
} from 'lucide-react';

import { RootState } from '../../store';
import {
  useLazyToolsSettingsQuery,
  useUpdateSettingMutation,
} from './settingsApi';
import { setCompanySettings } from './settingsSlice';
import Loader from '../../ui-components/Loader';
import { API_CONSTANTS } from '../../utils/constants';

interface SettingInstance {
  name: string;
  description: string;
  value: string;
  groupType: string;
  displayType: string;
  helpText: string;
  sourceValue?: any;
}

const CompanySettingPage = () => {
  const dispatch = useDispatch();
  const [toolsSettingsApi, { isLoading }] = useLazyToolsSettingsQuery();
  const [updateSetting, { isLoading: isUpdating }] = useUpdateSettingMutation();

  const companySettings =
    useSelector((store: RootState) => store.settings.companySettings) || [];
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [filePreviews, setFilePreviews] = useState<Record<string, string>>({});
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await toolsSettingsApi({ type: 'Company' }).unwrap();
        if (res?.settings?.status === 200) {
          dispatch(setCompanySettings(res.data));

          const initialValues: Record<string, string> = {};
          const previews: Record<string, string> = {};

          res.data.forEach((item: SettingInstance) => {
            initialValues[item.name] = item.value;
            if (item.displayType.toLowerCase() === 'file' && item.value) {
              const isUrl = item.value.startsWith('http');
              previews[item.name] = isUrl
                ? item.value
                : `${API_CONSTANTS.BASE_URL}public/upload/settings_files/${item.value}`;
            }
          });

          setFormValues(initialValues);
          setFilePreviews(previews);
        }
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
        toast.error('Failed to load settings');
      }
    }
    load();
  }, [dispatch, toolsSettingsApi]);

  const handleInputChange = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (name: string, file: File) => {
    setIsUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('setting_file', file);
      formData.append('setting_key', name);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_CONSTANTS.BASE_URL}api/v1/tools/settings-upload-files`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const res = await response.json();
        const status = res?.settings?.status || res?.statusCode || res?.status;

        if (status === 200 || status === 201) {
          toast.success('File uploaded and saved successfully');

          // The API returns the new filename or URL
          const newValue = res.data?.name || res.data?.fileName;
          if (newValue) {
            handleInputChange(name, newValue);
            const isUrl = newValue.startsWith('http');
            setFilePreviews((prev) => ({
              ...prev,
              [name]: isUrl
                ? newValue
                : `${API_CONSTANTS.BASE_URL}public/upload/settings_files/${newValue}`,
            }));
          }
        } else {
          toast.error(
            'File upload failed: ' + (res?.settings?.message || 'Unknown error')
          );
        }
      } else {
        toast.error(`Upload failed: ${response.status}`);
      }
    } catch (error) {
      console.error('File upload exception:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        setting: formValues,
      };
      await updateSetting(payload).unwrap();

      // Sync with redux
      const updatedSettings = companySettings.map((item: any) => ({
        ...item,
        value: formValues[item.name] ?? item.value,
      }));
      dispatch(setCompanySettings(updatedSettings));

      toast.success('Company settings saved successfully');
    } catch (error: any) {
      const message =
        error?.data?.settings?.message || 'Failed to save settings';
      toast.error(message);
    }
  };

  const handleReset = () => {
    const resetValues: Record<string, string> = {};
    const previews: Record<string, string> = {};

    companySettings.forEach((item: any) => {
      resetValues[item.name] = item.value;
      if (item.displayType.toLowerCase() === 'file' && item.value) {
        const isUrl = item.value.startsWith('http');
        previews[item.name] = isUrl
          ? item.value
          : `${API_CONSTANTS.BASE_URL}public/upload/settings_files/${item.value}`;
      }
    });

    setFormValues(resetValues);
    setFilePreviews(previews);
    toast.info('Settings reset to last saved state');
  };

  // Group items by groupType for the UI
  const groupedSettings = useMemo(() => {
    const groups: Record<string, SettingInstance[]> = {};
    companySettings.forEach((item) => {
      const group = item.groupType || 'General';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
    });

    // Map group names to Icons for better UI
    const groupIcons: Record<string, any> = {
      General: Building2,
      Address: MapPin,
      Social: Share2,
      Store: Share2,
      aws: Lock,
      'AWS Settings': Lock,
    };

    return Object.entries(groups).map(([name, items]) => ({
      name,
      icon:
        groupIcons[name] ||
        groupIcons[
          Object.keys(groupIcons).find((k) => name.includes(k)) || 'General'
        ] ||
        Building2,
      items: items.sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [companySettings]);

  if (isLoading) return <Loader />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-8">
        {groupedSettings.length > 0 ? (
          groupedSettings.map((group) => {
            const Icon = group.icon;
            return (
              <div
                key={group.name}
                className="bg-white dark:bg-neutral-950 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden"
              >
                <div className="px-8 py-5 border-b border-neutral-100 dark:border-neutral-800 flex items-center gap-3">
                  <div className="p-2 bg-brand-50 dark:bg-brand-900/20 rounded-lg text-brand-600 dark:text-brand-400">
                    <Icon size={20} />
                  </div>
                  <h2 className="text-lg font-bold text-neutral-900 dark:text-white uppercase tracking-wider">
                    {group.name}
                  </h2>
                </div>

                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    {group.items.map((item) => (
                      <div
                        key={item.name}
                        className={`flex flex-col gap-2 ${
                          item.displayType.toLowerCase() === 'file'
                            ? 'col-span-full'
                            : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-normal text-neutral-500 dark:text-neutral-400 uppercase tracking-tight">
                            {item.description}
                          </label>
                        </div>

                        {item.displayType.toLowerCase() === 'file' ? (
                          <div className="flex items-start gap-6">
                            <div className="relative group">
                              <div className="w-32 h-32 rounded-2xl border-2 border-dashed border-neutral-200 dark:border-neutral-800 flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 overflow-hidden">
                                {filePreviews[item.name] ? (
                                  <img
                                    src={filePreviews[item.name]}
                                    alt={item.description}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <ImageIcon
                                    className="text-neutral-300 dark:text-neutral-700"
                                    size={32}
                                  />
                                )}
                              </div>
                              <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-2xl">
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) =>
                                    e.target.files?.[0] &&
                                    handleFileUpload(
                                      item.name,
                                      e.target.files[0]
                                    )
                                  }
                                  accept="image/*"
                                />
                                <Upload className="text-white" size={24} />
                              </label>
                            </div>
                            <div className="flex-1 space-y-2">
                              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                {item.helpText ||
                                  `Upload ${item.description.toLowerCase()}. Recommended size: 512x512px.`}
                              </p>
                              <div className="flex gap-2">
                                <button
                                  className="px-4 py-2 text-xs font-bold text-brand-600 bg-brand-50 dark:bg-brand-900/20 rounded-lg hover:bg-brand-100 transition-colors"
                                  onClick={() => {
                                    const input =
                                      document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*';
                                    input.onchange = (e: any) =>
                                      e.target.files?.[0] &&
                                      handleFileUpload(
                                        item.name,
                                        e.target.files[0]
                                      );
                                    input.click();
                                  }}
                                >
                                  Choose New File
                                </button>
                                {filePreviews[item.name] && (
                                  <button
                                    className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                                    onClick={() => {
                                      handleInputChange(item.name, '');
                                      setFilePreviews((prev) => {
                                        const next = { ...prev };
                                        delete next[item.name];
                                        return next;
                                      });
                                    }}
                                  >
                                    <X size={18} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : item.displayType.toLowerCase() === 'checkbox' ? (
                          <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-900 rounded-xl border border-neutral-100 dark:border-neutral-800">
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">
                              Enabled
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  formValues[item.name] === 'Y' ||
                                  formValues[item.name] === 'yes'
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    item.name,
                                    e.target.checked ? 'Y' : 'N'
                                  )
                                }
                                disabled={isUpdating}
                              />
                              <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-brand-600"></div>
                            </label>
                          </div>
                        ) : item.displayType.toLowerCase() === 'selectbox' ? (
                          <div className="relative">
                            <select
                              value={formValues[item.name] || ''}
                              onChange={(e) =>
                                handleInputChange(item.name, e.target.value)
                              }
                              className="w-full h-12 px-4 appearance-none bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all text-sm"
                            >
                              {Array.isArray(item.sourceValue) ? (
                                item.sourceValue.map((opt: any) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.name}
                                  </option>
                                ))
                              ) : (
                                <option value={formValues[item.name]}>
                                  {formValues[item.name]}
                                </option>
                              )}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400">
                              <ChevronDown size={18} />
                            </div>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={formValues[item.name] || ''}
                            onChange={(e) =>
                              handleInputChange(item.name, e.target.value)
                            }
                            className="w-full h-12 px-4 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all text-sm"
                            placeholder={item.description}
                          />
                        )}
                        {item.helpText &&
                          item.displayType.toLowerCase() !== 'file' && (
                            <p className="text-xs text-neutral-400 mt-1">
                              {item.helpText}
                            </p>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-12 text-center bg-white dark:bg-neutral-950 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-800">
            <p className="text-neutral-500">No company settings found.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-6 pb-8 border-t border-neutral-100 dark:border-neutral-800">
        <button
          type="button"
          onClick={handleReset}
          disabled={isUpdating}
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all rounded-xl"
        >
          <RotateCcw size={18} />
          Reset to Saved
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isUpdating}
          className={`flex items-center gap-2 px-8 py-2.5 text-sm font-bold text-white transition-all rounded-xl uppercase tracking-wide ${
            isUpdating
              ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 cursor-not-allowed'
              : 'bg-brand-600 hover:bg-brand-700 shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40 active:scale-95'
          }`}
        >
          <Save size={18} />
          {isUpdating ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default CompanySettingPage;
